import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Clock, BookOpen, ChevronDown, X, Braces } from 'lucide-react';
import { api } from '../lib/api';

const ACCENT = '#f97316';
const HISTORY_KEY = 'bridge_graphql_history';
const MAX_HISTORY = 10;

interface SchemaQuery {
  name: string;
  description: string;
  args: { name: string; type: string; description: string }[];
  fields: { name: string; type: string; description: string }[];
}

interface HistoryEntry {
  query: string;
  variables: string;
  timestamp: number;
}

const EXAMPLE_QUERIES: { label: string; query: string; variables?: string }[] = [
  {
    label: 'List connections',
    query: '{\n  connections(limit: 10) {\n    id\n    name\n    connector_type\n    status\n    last_sync_at\n  }\n}',
  },
  {
    label: 'Get sync profile',
    query: '{\n  syncProfile(id: "YOUR_ID") {\n    id\n    name\n    status\n    last_sync_at\n    recent_runs\n  }\n}',
  },
  {
    label: 'Recent errors',
    query: '{\n  errors(limit: 20, status: "pending") {\n    id\n    error_message\n    category\n    status\n    created_at\n  }\n}',
  },
  {
    label: 'Active flows',
    query: '{\n  flows(limit: 10, status: "active") {\n    id\n    name\n    status\n    trigger_type\n    run_count\n    last_run_at\n  }\n}',
  },
  {
    label: 'Flow analytics (7 days)',
    query: '{\n  analytics(days: 7) {\n    id\n    flow_id\n    date\n    executions\n    successes\n    failures\n    avg_duration_ms\n  }\n}',
  },
  {
    label: 'Multi-query example',
    query: '{\n  connections(limit: 5) {\n    id\n    name\n    status\n  }\n  errors(limit: 5, status: "pending") {\n    id\n    error_message\n    category\n  }\n}',
  },
];

export default function GraphQLPage() {
  const [query, setQuery] = useState(EXAMPLE_QUERIES[0].query);
  const [variables, setVariables] = useState('{}');
  const [response, setResponse] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [schema, setSchema] = useState<SchemaQuery[]>([]);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [showExamples, setShowExamples] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showSchema, setShowSchema] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load history from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(HISTORY_KEY);
      if (stored) setHistory(JSON.parse(stored));
    } catch { /* ignore */ }
  }, []);

  // Load schema
  useEffect(() => {
    api.get<{ queries: SchemaQuery[] }>('/graphql/schema')
      .then(res => setSchema(res.queries))
      .catch(() => { /* schema load failed, non-critical */ });
  }, []);

  const saveHistory = useCallback((q: string, v: string) => {
    const entry: HistoryEntry = { query: q, variables: v, timestamp: Date.now() };
    const updated = [entry, ...history.filter(h => h.query !== q)].slice(0, MAX_HISTORY);
    setHistory(updated);
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(updated)); } catch { /* ignore */ }
  }, [history]);

  const executeQuery = useCallback(async () => {
    setLoading(true);
    setResponse('');
    try {
      let parsedVars = {};
      try {
        parsedVars = variables.trim() ? JSON.parse(variables) : {};
      } catch {
        setResponse(JSON.stringify({ errors: [{ message: 'Invalid JSON in variables' }] }, null, 2));
        setLoading(false);
        return;
      }

      const result = await api.post<Record<string, unknown>>('/graphql', {
        query,
        variables: parsedVars,
      });
      setResponse(JSON.stringify(result, null, 2));
      saveHistory(query, variables);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setResponse(JSON.stringify({ errors: [{ message: msg }] }, null, 2));
    } finally {
      setLoading(false);
    }
  }, [query, variables, saveHistory]);

  // Tab support in textarea
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const target = e.currentTarget;
      const start = target.selectionStart;
      const end = target.selectionEnd;
      const newValue = query.substring(0, start) + '  ' + query.substring(end);
      setQuery(newValue);
      requestAnimationFrame(() => {
        target.selectionStart = target.selectionEnd = start + 2;
      });
    }
    // Ctrl/Cmd+Enter to execute
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      executeQuery();
    }
  };

  const insertExample = (ex: { query: string; variables?: string }) => {
    setQuery(ex.query);
    if (ex.variables) setVariables(ex.variables);
    setShowExamples(false);
  };

  const insertSchemaQuery = (sq: SchemaQuery) => {
    const argsStr = sq.args.length > 0
      ? `(${sq.args.map(a => `${a.name}: ${a.type.includes('!') ? '"..."' : a.type === 'Int' ? '10' : '"..."'}`).join(', ')})`
      : '';
    const fieldsStr = sq.fields.map(f => `    ${f.name}`).join('\n');
    setQuery(`{\n  ${sq.name}${argsStr} {\n${fieldsStr}\n  }\n}`);
    setShowSchema(false);
  };

  const loadHistory = (entry: HistoryEntry) => {
    setQuery(entry.query);
    setVariables(entry.variables);
    setShowHistory(false);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Braces size={24} color={ACCENT} />
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>GraphQL Explorer</h1>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
              Query Bridge data with a GraphQL-like API
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {/* Examples dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => { setShowExamples(!showExamples); setShowHistory(false); setShowSchema(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer',
                fontSize: '0.8125rem', color: '#374151',
              }}
            >
              <BookOpen size={14} /> Examples <ChevronDown size={12} />
            </button>
            {showExamples && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', marginTop: '0.25rem',
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 20, minWidth: 220,
              }}>
                {EXAMPLE_QUERIES.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => insertExample(ex)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: '0.5rem 0.75rem', border: 'none', background: 'none',
                      cursor: 'pointer', fontSize: '0.8125rem', color: '#374151',
                      borderBottom: i < EXAMPLE_QUERIES.length - 1 ? '1px solid #f3f4f6' : 'none',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                  >
                    {ex.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* History dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => { setShowHistory(!showHistory); setShowExamples(false); setShowSchema(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
                border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer',
                fontSize: '0.8125rem', color: '#374151',
              }}
            >
              <Clock size={14} /> History
            </button>
            {showHistory && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', marginTop: '0.25rem',
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 20, minWidth: 300, maxHeight: 300, overflowY: 'auto',
              }}>
                {history.length === 0 ? (
                  <div style={{ padding: '0.75rem', color: '#9ca3af', fontSize: '0.8125rem' }}>No history yet</div>
                ) : (
                  history.map((entry, i) => (
                    <button
                      key={i}
                      onClick={() => loadHistory(entry)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '0.5rem 0.75rem', border: 'none', background: 'none',
                        cursor: 'pointer', fontSize: '0.75rem', color: '#374151',
                        borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                    >
                      <div style={{ color: '#9ca3af', fontSize: '0.6875rem', marginBottom: '0.25rem' }}>
                        {new Date(entry.timestamp).toLocaleString()}
                      </div>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280 }}>
                        {entry.query.replace(/\n/g, ' ').substring(0, 80)}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Schema toggle */}
          <button
            onClick={() => { setShowSchema(!showSchema); setShowExamples(false); setShowHistory(false); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.5rem 0.75rem', borderRadius: '0.375rem',
              border: showSchema ? `1px solid ${ACCENT}` : '1px solid #d1d5db',
              background: showSchema ? '#fff7ed' : '#fff', cursor: 'pointer',
              fontSize: '0.8125rem', color: showSchema ? ACCENT : '#374151',
            }}
          >
            <Braces size={14} /> Schema
          </button>
        </div>
      </div>

      {/* Main panels */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        {/* Left: query editor */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem',
            overflow: 'hidden', flex: 1,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.5rem 0.75rem', borderBottom: '1px solid #f3f4f6', background: '#fafafa',
            }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Query
              </span>
              <button
                onClick={executeQuery}
                disabled={loading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.375rem',
                  padding: '0.375rem 0.75rem', borderRadius: '0.375rem',
                  border: 'none', background: loading ? '#d1d5db' : ACCENT,
                  color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '0.8125rem', fontWeight: 600,
                }}
              >
                <Play size={14} fill="#fff" />
                {loading ? 'Running...' : 'Execute'}
              </button>
            </div>
            <textarea
              ref={textareaRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              spellCheck={false}
              style={{
                width: '100%', minHeight: 280, padding: '0.75rem',
                fontFamily: '"SF Mono", "Fira Code", "Cascadia Code", monospace',
                fontSize: '0.8125rem', lineHeight: 1.6, border: 'none', outline: 'none',
                resize: 'vertical', background: '#1e1e2e', color: '#cdd6f4',
                boxSizing: 'border-box',
              }}
              placeholder="{ connections(limit: 10) { id name status } }"
            />
          </div>

          {/* Variables panel */}
          <div style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden',
          }}>
            <div style={{
              padding: '0.5rem 0.75rem', borderBottom: '1px solid #f3f4f6', background: '#fafafa',
            }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Variables
              </span>
            </div>
            <textarea
              value={variables}
              onChange={e => setVariables(e.target.value)}
              spellCheck={false}
              style={{
                width: '100%', minHeight: 80, padding: '0.75rem',
                fontFamily: '"SF Mono", "Fira Code", "Cascadia Code", monospace',
                fontSize: '0.8125rem', lineHeight: 1.6, border: 'none', outline: 'none',
                resize: 'vertical', background: '#1e1e2e', color: '#cdd6f4',
                boxSizing: 'border-box',
              }}
              placeholder='{ "id": "abc123" }'
            />
          </div>

          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
            Press Ctrl+Enter (Cmd+Enter) to execute. Tab inserts spaces.
          </div>
        </div>

        {/* Right: response viewer */}
        <div style={{
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0.5rem 0.75rem', borderBottom: '1px solid #f3f4f6', background: '#fafafa',
          }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Response
            </span>
            {response && (
              <button
                onClick={() => setResponse('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '0.125rem' }}
              >
                <X size={14} />
              </button>
            )}
          </div>
          <pre style={{
            flex: 1, margin: 0, padding: '0.75rem',
            fontFamily: '"SF Mono", "Fira Code", "Cascadia Code", monospace',
            fontSize: '0.8125rem', lineHeight: 1.6,
            background: '#1e1e2e', color: '#cdd6f4',
            overflow: 'auto', minHeight: 360, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {response || (loading ? 'Executing query...' : 'Results will appear here after executing a query.')}
          </pre>
        </div>
      </div>

      {/* Schema browser */}
      {showSchema && (
        <div style={{
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem',
          overflow: 'hidden', marginTop: '0.5rem',
        }}>
          <div style={{
            padding: '0.75rem', borderBottom: '1px solid #f3f4f6', background: '#fafafa',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
              Schema Browser
            </span>
            <button
              onClick={() => setShowSchema(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}
            >
              <X size={16} />
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.75rem', padding: '0.75rem' }}>
            {schema.map(sq => (
              <div
                key={sq.name}
                style={{
                  border: '1px solid #e5e7eb', borderRadius: '0.375rem', padding: '0.75rem',
                  cursor: 'pointer', transition: 'border-color 0.15s',
                }}
                onClick={() => insertSchemaQuery(sq)}
                onMouseEnter={e => (e.currentTarget.style.borderColor = ACCENT)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = '#e5e7eb')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 600, color: ACCENT }}>{sq.name}</span>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0 0 0.5rem' }}>{sq.description}</p>

                {sq.args.length > 0 && (
                  <div style={{ marginBottom: '0.375rem' }}>
                    <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Args</div>
                    {sq.args.map(a => (
                      <div key={a.name} style={{ fontSize: '0.75rem', color: '#374151', display: 'flex', gap: '0.375rem' }}>
                        <span style={{ fontFamily: 'monospace', color: '#7c3aed' }}>{a.name}</span>
                        <span style={{ color: '#9ca3af' }}>({a.type})</span>
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <div style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Fields</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {sq.fields.map(f => (
                      <span
                        key={f.name}
                        style={{
                          fontSize: '0.6875rem', fontFamily: 'monospace',
                          background: '#f3f4f6', padding: '0.125rem 0.375rem', borderRadius: '0.25rem',
                          color: '#374151',
                        }}
                      >
                        {f.name}
                      </span>
                    ))}
                  </div>
                </div>

                <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  Click to insert query
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
